import React, { useState } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Chip,
  IconButton,
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tabs,
  Tab,
  Alert,
  useTheme,
  Divider,
  Tooltip,
} from "@mui/material";
import {
  School as SchoolIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  VpnKey as VpnKeyIcon,
  Palette as PaletteIcon,
  WhatsApp as WhatsAppIcon,
  Settings as SettingsIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  AccountCircle as AccountCircleIcon,
  NotificationsActive as NotificationsActiveIcon,
  Schedule as ScheduleIcon,
} from "@mui/icons-material";
import { motion } from "framer-motion";
import toast from "react-hot-toast";

const SchoolAccounts = () => {
  const theme = useTheme();
  const [selectedTab, setSelectedTab] = useState(0);
  const [accountDialogOpen, setAccountDialogOpen] = useState(false);
  const [themeDialogOpen, setThemeDialogOpen] = useState(false);
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const [schoolAccounts, setSchoolAccounts] = useState([
    {
      id: 1,
      schoolId: 1,
      schoolName: "Greenwood High School",
      accountName: "Greenwood WhatsApp",
      phoneNumber: "+91 98765 43210",
      apiKey: "wa_xxxxxxxxxxxxx",
      status: "Active",
      theme: "Professional",
      messageCount: 1250,
      lastActive: "2024-01-15 10:30 AM",
      createdAt: "2024-01-01",
      autoResponder: true,
      scheduledMessages: true,
    },
    {
      id: 2,
      schoolId: 2,
      schoolName: "Sunshine Academy",
      accountName: "Sunshine Messaging",
      phoneNumber: "+91 98765 43211",
      apiKey: "wa_yyyyyyyyyyyyy",
      status: "Active",
      theme: "Friendly",
      messageCount: 890,
      lastActive: "2024-01-14 3:45 PM",
      createdAt: "2024-01-05",
      autoResponder: false,
      scheduledMessages: true,
    },
    {
      id: 3,
      schoolId: 3,
      schoolName: "Riverside School",
      accountName: "Riverside Comm",
      phoneNumber: "+91 98765 43212",
      apiKey: "wa_zzzzzzzzzzzzz",
      status: "Inactive",
      theme: "Formal",
      messageCount: 450,
      lastActive: "2024-01-10 9:15 AM",
      createdAt: "2024-01-08",
      autoResponder: true,
      scheduledMessages: false,
    },
  ]);

  const [themes, setThemes] = useState([
    {
      id: 1,
      name: "Professional",
      description: "Formal and business-like messaging style",
      greeting: "Dear {contact_person},",
      closing: "Best regards,\n{school_name} Team",
      tone: "Professional",
      color: "#4169E1",
    },
    {
      id: 2,
      name: "Friendly",
      description: "Warm and approachable messaging style",
      greeting: "Hi {contact_person}!",
      closing: "Thanks!\n{school_name}",
      tone: "Friendly",
      color: "#25D366",
    },
    {
      id: 3,
      name: "Formal",
      description: "Very formal and respectful messaging style",
      greeting: "Respected {contact_person},",
      closing: "Yours sincerely,\n{school_name} Administration",
      tone: "Formal",
      color: "#1E3A8A",
    },
    {
      id: 4,
      name: "Casual",
      description: "Relaxed and conversational messaging style",
      greeting: "Hey {contact_person}!",
      closing: "Cheers!\n{school_name}",
      tone: "Casual",
      color: "#FF6B6B",
    },
  ]);

  const [newAccount, setNewAccount] = useState({
    schoolId: "",
    accountName: "",
    phoneNumber: "",
    theme: "Professional",
    autoResponder: false,
    scheduledMessages: true,
  });

  const schools = [
    { id: 1, name: "Greenwood High School", status: "Billed" },
    { id: 2, name: "Sunshine Academy", status: "Demo" },
    { id: 3, name: "Riverside School", status: "Billed" },
    { id: 4, name: "Mountain View School", status: "Not Billed" },
    { id: 5, name: "City Central School", status: "Billed" },
    { id: 6, name: "Oakwood Academy", status: "Expired" },
  ];

  const filteredAccounts = schoolAccounts.filter((account) => {
    const matchesSearch =
      account.schoolName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      account.accountName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      account.phoneNumber.includes(searchTerm);
    const matchesStatus = statusFilter === "all" || account.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const handleCreateAccount = () => {
    if (!newAccount.schoolId || !newAccount.accountName || !newAccount.phoneNumber) {
      toast.error("Please fill all required fields");
      return;
    }

    const school = schools.find((s) => s.id === parseInt(newAccount.schoolId));
    const theme = themes.find((t) => t.name === newAccount.theme);

    const account = {
      id: Date.now(),
      schoolId: parseInt(newAccount.schoolId),
      schoolName: school.name,
      accountName: newAccount.accountName,
      phoneNumber: newAccount.phoneNumber,
      apiKey: `wa_${Math.random().toString(36).substr(2, 15)}`,
      status: "Active",
      theme: newAccount.theme,
      messageCount: 0,
      lastActive: new Date().toLocaleString(),
      createdAt: new Date().toISOString().split("T")[0],
      autoResponder: newAccount.autoResponder,
      scheduledMessages: newAccount.scheduledMessages,
    };

    setSchoolAccounts([...schoolAccounts, account]);
    toast.success("School account created successfully!");
    setAccountDialogOpen(false);
    setNewAccount({
      schoolId: "",
      accountName: "",
      phoneNumber: "",
      theme: "Professional",
      autoResponder: false,
      scheduledMessages: true,
    });
  };

  const handleDeleteAccount = (id) => {
    if (window.confirm("Are you sure you want to delete this account?")) {
      setSchoolAccounts(schoolAccounts.filter((account) => account.id !== id));
      toast.success("Account deleted successfully!");
    }
  };

  const handleToggleStatus = (id) => {
    setSchoolAccounts(
      schoolAccounts.map((account) =>
        account.id === id
          ? { ...account, status: account.status === "Active" ? "Inactive" : "Active" }
          : account
      )
    );
    toast.success("Account status updated!");
  };

  const handleApplyTheme = (accountId, themeName) => {
    setSchoolAccounts(
      schoolAccounts.map((account) =>
        account.id === accountId ? { ...account, theme: themeName } : account
      )
    );
    toast.success("Theme applied successfully!");
    setThemeDialogOpen(false);
  };

  const getStatusColor = (status) => {
    return status === "Active" ? "success" : "default";
  };

  return (
    <Box>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        {/* Header */}
        <Box sx={{ mb: 4, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, display: "flex", alignItems: "center", gap: 1 }}>
              <AccountCircleIcon sx={{ color: theme.palette.primary.main, fontSize: "2rem" }} />
              School WhatsApp Accounts
            </Typography>
            <Typography variant="body1" sx={{ color: "text.secondary" }}>
              Manage school accounts for theme-based messaging services
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setAccountDialogOpen(true)}
          >
            Create Account
          </Button>
        </Box>

        {/* Tabs */}
        <Card sx={{ mb: 3 }}>
          <Tabs
            value={selectedTab}
            onChange={(e, newValue) => setSelectedTab(newValue)}
            sx={{ borderBottom: 1, borderColor: "divider" }}
          >
            <Tab label="Accounts" />
            <Tab label="Themes" />
            <Tab label="Settings" />
          </Tabs>
        </Card>

        {/* Accounts Tab */}
        {selectedTab === 0 && (
          <>
            {/* Filters */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      placeholder="Search accounts..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      InputProps={{
                        startAdornment: <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />,
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Filter by Status</InputLabel>
                      <Select
                        value={statusFilter}
                        label="Filter by Status"
                        onChange={(e) => setStatusFilter(e.target.value)}
                      >
                        <MenuItem value="all">All Status</MenuItem>
                        <MenuItem value="Active">Active</MenuItem>
                        <MenuItem value="Inactive">Inactive</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Accounts Table */}
            <Card>
              <CardContent>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>School</TableCell>
                        <TableCell>Account Name</TableCell>
                        <TableCell>Phone Number</TableCell>
                        <TableCell>Theme</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Messages</TableCell>
                        <TableCell>Features</TableCell>
                        <TableCell>Last Active</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredAccounts.map((account) => (
                        <TableRow key={account.id} hover>
                          <TableCell>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <Avatar sx={{ bgcolor: theme.palette.primary.main, width: 32, height: 32 }}>
                                <SchoolIcon fontSize="small" />
                              </Avatar>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {account.schoolName}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>{account.accountName}</TableCell>
                          <TableCell>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                              <WhatsAppIcon fontSize="small" sx={{ color: "#25D366" }} />
                              {account.phoneNumber}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={account.theme}
                              size="small"
                              sx={{
                                bgcolor: themes.find((t) => t.name === account.theme)?.color || theme.palette.primary.main,
                                color: "white",
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={account.status}
                              size="small"
                              color={getStatusColor(account.status)}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {account.messageCount.toLocaleString()}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: "flex", gap: 0.5 }}>
                              {account.autoResponder && (
                                <Tooltip title="Auto Responder">
                                  <NotificationsActiveIcon fontSize="small" color="primary" />
                                </Tooltip>
                              )}
                              {account.scheduledMessages && (
                                <Tooltip title="Scheduled Messages">
                                  <ScheduleIcon fontSize="small" color="primary" />
                                </Tooltip>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" sx={{ color: "text.secondary" }}>
                              {account.lastActive}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Box sx={{ display: "flex", gap: 0.5, justifyContent: "flex-end" }}>
                              <Tooltip title="Change Theme">
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setSelectedSchool(account);
                                    setThemeDialogOpen(true);
                                  }}
                                >
                                  <PaletteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Toggle Status">
                                <IconButton
                                  size="small"
                                  onClick={() => handleToggleStatus(account.id)}
                                >
                                  {account.status === "Active" ? (
                                    <CheckCircleIcon fontSize="small" color="success" />
                                  ) : (
                                    <CancelIcon fontSize="small" color="error" />
                                  )}
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleDeleteAccount(account.id)}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </>
        )}

        {/* Themes Tab */}
        {selectedTab === 1 && (
          <Grid container spacing={3}>
            {themes.map((theme) => (
              <Grid item xs={12} md={6} key={theme.id}>
                <Card
                  sx={{
                    border: `2px solid ${theme.color}`,
                    "&:hover": {
                      boxShadow: 4,
                      transform: "translateY(-4px)",
                    },
                    transition: "all 0.3s ease",
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {theme.name}
                      </Typography>
                      <Chip
                        label={theme.tone}
                        size="small"
                        sx={{ bgcolor: theme.color, color: "white" }}
                      />
                    </Box>
                    <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
                      {theme.description}
                    </Typography>
                    <Divider sx={{ my: 2 }} />
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                        Greeting:
                      </Typography>
                      <Typography variant="body2" sx={{ fontStyle: "italic", color: "text.secondary" }}>
                        {theme.greeting}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                        Closing:
                      </Typography>
                      <Typography variant="body2" sx={{ fontStyle: "italic", color: "text.secondary", whiteSpace: "pre-line" }}>
                        {theme.closing}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Settings Tab */}
        {selectedTab === 2 && (
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                Account Settings
              </Typography>
              <Alert severity="info" sx={{ mb: 3 }}>
                Configure default settings for all school WhatsApp accounts
              </Alert>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={<Switch defaultChecked />}
                    label="Enable Auto-Responder by Default"
                  />
                  <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 1 }}>
                    Automatically respond to incoming messages
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={<Switch defaultChecked />}
                    label="Enable Scheduled Messages by Default"
                  />
                  <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 1 }}>
                    Allow schools to schedule messages
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={<Switch />}
                    label="Require API Key Verification"
                  />
                  <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 1 }}>
                    Verify API keys before account activation
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={<Switch defaultChecked />}
                    label="Send Usage Reports"
                  />
                  <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 1 }}>
                    Send monthly usage reports to schools
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}

        {/* Create Account Dialog */}
        <Dialog open={accountDialogOpen} onClose={() => setAccountDialogOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>Create School WhatsApp Account</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Select School</InputLabel>
                  <Select
                    value={newAccount.schoolId}
                    label="Select School"
                    onChange={(e) => setNewAccount({ ...newAccount, schoolId: e.target.value })}
                  >
                    {schools
                      .filter((school) => !schoolAccounts.find((acc) => acc.schoolId === school.id))
                      .map((school) => (
                        <MenuItem key={school.id} value={school.id}>
                          {school.name}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Account Name"
                  value={newAccount.accountName}
                  onChange={(e) => setNewAccount({ ...newAccount, accountName: e.target.value })}
                  placeholder="e.g., School WhatsApp Account"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="WhatsApp Phone Number"
                  value={newAccount.phoneNumber}
                  onChange={(e) => setNewAccount({ ...newAccount, phoneNumber: e.target.value })}
                  placeholder="+91 98765 43210"
                  helperText="Include country code"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Select Theme</InputLabel>
                  <Select
                    value={newAccount.theme}
                    label="Select Theme"
                    onChange={(e) => setNewAccount({ ...newAccount, theme: e.target.value })}
                  >
                    {themes.map((theme) => (
                      <MenuItem key={theme.id} value={theme.name}>
                        {theme.name} - {theme.description}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={newAccount.autoResponder}
                      onChange={(e) => setNewAccount({ ...newAccount, autoResponder: e.target.checked })}
                    />
                  }
                  label="Enable Auto-Responder"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={newAccount.scheduledMessages}
                      onChange={(e) => setNewAccount({ ...newAccount, scheduledMessages: e.target.checked })}
                    />
                  }
                  label="Enable Scheduled Messages"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAccountDialogOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleCreateAccount}>
              Create Account
            </Button>
          </DialogActions>
        </Dialog>

        {/* Theme Selection Dialog */}
        <Dialog open={themeDialogOpen} onClose={() => setThemeDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Change Theme for {selectedSchool?.schoolName}</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              {themes.map((theme) => (
                <Grid item xs={12} key={theme.id}>
                  <Card
                    sx={{
                      cursor: "pointer",
                      border: selectedSchool?.theme === theme.name ? `2px solid ${theme.color}` : "1px solid",
                      borderColor: selectedSchool?.theme === theme.name ? theme.color : "divider",
                      "&:hover": {
                        borderColor: theme.color,
                        boxShadow: 2,
                      },
                    }}
                    onClick={() => handleApplyTheme(selectedSchool?.id, theme.name)}
                  >
                    <CardContent>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {theme.name}
                          </Typography>
                          <Typography variant="caption" sx={{ color: "text.secondary" }}>
                            {theme.description}
                          </Typography>
                        </Box>
                        <Chip
                          label={theme.tone}
                          size="small"
                          sx={{ bgcolor: theme.color, color: "white" }}
                        />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setThemeDialogOpen(false)}>Cancel</Button>
          </DialogActions>
        </Dialog>
      </motion.div>
    </Box>
  );
};

export default SchoolAccounts;

